import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FileText, Users, Trash2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from '../components/Brand';
import { PUBLIC_SITE_NAME, SIGNED_IN_PRODUCT_NAME } from '../config/brand';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Feature = ({ icon: Icon, title, text }) => (
  <div className="flex gap-3">
    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0"><Icon className="w-4.5 h-4.5 w-[18px] h-[18px] text-white" /></div>
    <div>
      <div className="text-[14px] font-semibold text-white">{title}</div>
      <div className="text-[13px] text-white/70">{text}</div>
    </div>
  </div>
);

export default function Login() {
  const { user, loading, login, loginWithPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await loginWithPassword(email.trim(), password);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not sign you in. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (user) return <Navigate to="/workspace" replace />;

  return (
    <div className="min-h-screen w-full flex bg-[#F5F6FB]" data-testid="login-page">
      <div className="hidden lg:flex w-[46%] bg-[#0A1551] p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2 w-fit"><BrandLogo variant="public" light /></Link>
        <div className="space-y-8">
          <h2 className="text-[34px] font-extrabold text-white leading-tight">Your forms, teams and data.<br />All in one workspace.</h2>
          <div className="space-y-5">
            <Feature icon={FileText} title="Private workspace" text="Only you can see the assets you create." />
            <Feature icon={Users} title="Team workspaces" text="Invite teammates by email and share assets." />
            <Feature icon={Trash2} title="30-day trash" text="Deleted items can be restored for 30 days." />
            <Feature icon={ShieldCheck} title="Secure sign-in" text="Sign in with Google or your team password." />
          </div>
        </div>
        <div className="text-[12px] text-white/50">© {new Date().getFullYear()} {PUBLIC_SITE_NAME} · {SIGNED_IN_PRODUCT_NAME}</div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-[420px] bg-white border border-[#E3E5F5] rounded-2xl shadow-sm p-8">
          <h1 className="text-[26px] font-bold text-[#0A1551]">Welcome to {SIGNED_IN_PRODUCT_NAME}</h1>
          <p className="text-[14px] text-[#6F76A7] mt-1 mb-6">Sign in or create your free workspace with Google.</p>
          <button
            onClick={login}
            data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-md border border-[#D5D7ED] bg-white hover:bg-[#F5F6FB] text-[15px] font-semibold text-[#0A1551] transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <span className="flex-1 h-px bg-[#E3E5F5]" />
            <span className="text-[12px] text-[#9599C0]">or sign in with email</span>
            <span className="flex-1 h-px bg-[#E3E5F5]" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="password-login-form">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" data-testid="login-email-input"
              className="w-full px-3 py-2.5 border border-[#D5D7ED] rounded-md text-[15px] text-[#0A1551] focus:outline-none focus:border-[#0A1551]" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" data-testid="login-password-input"
              className="w-full px-3 py-2.5 border border-[#D5D7ED] rounded-md text-[15px] text-[#0A1551] focus:outline-none focus:border-[#0A1551]" />
            {error && <p className="text-[13px] text-[#E5387B]" role="alert" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={!email || !password || busy} data-testid="login-submit"
              className="w-full px-4 py-3 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[15px] font-semibold disabled:opacity-50">
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-[12px] text-[#9599C0] mt-6 text-center">
            By continuing you agree to the Terms of Service and Privacy Policy.
          </p>
        </div>

        <Link to="/" data-testid="login-back-link"
          className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#D5D7ED] bg-white text-[14px] font-semibold text-[#0A1551] hover:bg-[#F0F1FB] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>
    </div>
  );
}
